<div class="portal-page">

	<!-- Shared: revision timeline + project-total summary (mobile card + modal).
	     Defined at the component root so both the list and the modal can use it. -->
	<ng-template #coDetailBlock let-tl="tl" let-coVal="coVal" let-prev="prev" let-prevLbl="prevLbl" let-newTotal="newTotal" let-revLbl="revLbl" let-cur="cur">
		<div class="co-timeline mb-2">
			<div class="co-tl-item d-flex gap-2" *ngFor="let t of tl; let last = last"
				[class.co-tl-last]="last" [class.co-tl-current]="t.current" [class.co-tl-accepted]="t.status === 'accepted'">
				<div class="co-tl-rail d-flex flex-column align-items-center">
					<span class="co-tl-dot"><i class="wq wq-check" *ngIf="t.status === 'accepted'"></i></span>
					<span class="co-tl-line" *ngIf="!last"></span>
				</div>
				<div class="co-tl-body pb-3">
					<div class="d-flex align-items-center flex-wrap gap-2">
						<span class="co-tl-title">{{ t.title }}</span>
						<span class="badge" [ngClass]="{'badge-success': t.status === 'accepted', 'badge-warning': t.status === 'pending', 'badge-primary': t.status === 'declined', 'badge-secondary': t.status === 'draft'}">{{ t.status_label }}</span>
						<span class="qr-date qr-date-pending" *ngIf="t.valid_until">Valid until {{ t.valid_until | localizedDate }}</span>
					</div>
					<div class="co-tl-desc" *ngIf="t.description">{{ t.description }}</div>
				</div>
			</div>
		</div>

		<div class="co-sum-table">
			<div class="co-sum-row d-flex align-items-center justify-content-between px-3 py-3">
				<span>{{ prevLbl }} Project Total</span>
				<span class="font-weight-bold">{{ prev | currency:cur }}</span>
			</div>
			<div class="co-sum-row d-flex align-items-center justify-content-between px-3 py-3">
				<span>Change Order {{ revLbl }}</span>
				<span class="font-weight-bold" [class.text-success-solid]="coVal > 0" [class.text-danger-solid]="coVal < 0">{{ signedMoney(coVal, cur) }}</span>
			</div>
			<div class="co-sum-row co-sum-total d-flex align-items-center justify-content-between px-3 py-3">
				<span class="font-weight-bold">New Project Total <span class="small text-muted d-block">if accepted</span></span>
				<span class="font-weight-bold text-success-solid">{{ newTotal | currency:cur }}</span>
			</div>
		</div>
	</ng-template>

	<!-- Top bar -->
	<portal-header mode="section" [projectId]="projectId" current="change_orders" [sections]="sections"></portal-header>

	<portal-loader *ngIf="loading"></portal-loader>
	<div class="sec-state portal-error" *ngIf="error">{{ error }}</div>

	<ng-container *ngIf="!loading && !error">

		<!-- Summary -->
		<portal-project-totals [projectId]="projectId"></portal-project-totals>

		<!-- Filter tabs -->
		<div class="sec-filter-tabs">
			<button class="portal-pill" *ngFor="let tab of tabs" [class.active]="filter === tab.key" (click)="filter = tab.key">
				{{ tab.label }} <span class="portal-pill-count">{{ counts[tab.key] || 0 }}</span>
			</button>
			<label class="sec-search-pill">
				<i class="wq wq-search"></i>
				<input type="text" placeholder="Search" [(ngModel)]="search">
			</label>
		</div>

		<!-- Change order rows -->
		<div class="sec-list">
			<p class="sec-state" *ngIf="!filtered.length">No change orders to show.</p>

			<div class="co-card p-3 mb-2" *ngFor="let o of filtered">
				<!-- Desktop: compact row -->
				<div class="co-desktop-only d-flex flex-wrap justify-content-between gap-3">
					<div class="d-flex flex-fill gap-3" style="min-width:0">
						<div class="qr-thumb"><img class="qr-thumb-img" [src]="o.image_url || 'assets/image-placeholder.png'" alt=""></div>

						<div class="d-flex flex-column flex-fill gap-3" style="min-width:0">
							<div class="d-flex flex-wrap gap-3">
								<!-- Name: quote meta + title -->
								<div class="co-name-col">
									<div class="qr-meta">
										<span class="qr-number">Quote #{{ o.quote_no }}</span>
										<span class="qr-revision">Change Order {{ coRevisionLabel(o) }}</span>
									</div>
									<div class="qr-title">{{ o.title }}</div>
								</div>

								<!-- Status, version, description -->
								<div class="d-flex flex-column flex-fill gap-2" style="min-width:0">
									<div class="qr-status">
										<span class="badge" [ngClass]="badgeClass(o.status)">{{ badgeLabel(o.status) }}</span>
										<span class="qr-date" [ngClass]="dateLabel(o).cls">{{ dateLabel(o).text }}</span>
									</div>
									<div class="d-flex align-items-center gap-1 co-version">
										{{ o.prev_count }} Previous Version <i class="wq wq-arrow-right"></i>
										<span class="co-ver-co">Change Order {{ coRevisionLabel(o) }}</span>
									</div>
									<div class="co-desc" *ngIf="o.co_description">{{ o.co_description }}</div>
								</div>
							</div>

							<div class="d-flex flex-wrap co-figures">
								<div class="d-flex flex-column">
									<span class="co-fig-val">{{ (o.status === 'accepted' ? o.previous_project_total : o.current_project_total) | currency:o.currency_id }}</span>
									<span class="co-fig-lbl">{{ o.status === 'accepted' ? 'Previous' : 'Current' }} Project Total</span>
								</div>
								<div class="d-flex flex-column">
									<span class="co-fig-val" [class.text-success-solid]="o.change_value > 0" [class.text-danger-solid]="o.change_value < 0">{{ signedMoney(o.change_value, o.currency_id) }}</span>
									<span class="co-fig-lbl">Change Order {{ coRevisionLabel(o) }}</span>
								</div>
								<div class="d-flex flex-column">
									<span class="co-fig-val text-success-solid">{{ o.new_project_total | currency:o.currency_id }}</span>
									<span class="co-fig-lbl">New Project Total<span *ngIf="o.status === 'accepted'"> · After Change Order {{ coRevisionLabel(o) }} accepted</span></span>
								</div>
							</div>
						</div>
					</div>

					<div class="d-flex flex-column align-items-end justify-content-between gap-2 co-side">
						<portal-button (click)="detailsModal.open(o)">View Change Order Details</portal-button>
						<div class="portal-btn-group" *ngIf="isPending(o)">
							<!-- Add a comment: opens the proposal with the comment modal auto-opened. -->
							<portal-button icon="wq wq-notes" [title]="preview.ready ? preview.blockedReason : (o.can_comment ? 'Add a comment' : 'No proposal published for this change order')" aria-label="Add a comment" (click)="commentOrder(o)" [disabled]="preview.ready || !o.can_comment"></portal-button>
							<portal-button *ngIf="canConfirm(o)" (click)="openToBeConfirmed(o)" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">To be confirmed</portal-button>
							<portal-button (click)="openReject(o)" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">Decline</portal-button>
							<portal-button (click)="acceptOrder(o)" [disabled]="preview.ready || !o.can_accept" [title]="preview.ready ? preview.blockedReason : (o.can_accept ? null : 'No proposal published for this change order')">Accept</portal-button>
						</div>
					</div>
				</div>

				<!-- Mobile: expanded card (header, timeline + summary, view, actions) -->
				<div class="co-mobile-only">
					<div class="d-flex gap-2">
						<div class="qr-thumb"><img class="qr-thumb-img" [src]="o.image_url || 'assets/image-placeholder.png'" alt=""></div>
						<div class="d-flex flex-column flex-fill" style="min-width:0">
							<div class="qr-status mb-1">
								<span class="badge" [ngClass]="badgeClass(o.status)">{{ badgeLabel(o.status) }}</span>
								<span class="qr-date" [ngClass]="dateLabel(o).cls">{{ dateLabel(o).text }}</span>
							</div>
							<div class="qr-number">Quote #{{ o.quote_no }}</div>
							<div class="qr-title">{{ o.title }}</div>
						</div>
					</div>

					<div class="my-3" style="border-top:1px solid var(--c-border-1)"></div>

					<ng-container *ngTemplateOutlet="coDetailBlock; context: {
						tl: o.timeline, coVal: o.change_value,
						prev: (o.status === 'accepted' ? o.previous_project_total : o.current_project_total),
						prevLbl: (o.status === 'accepted' ? 'Previous' : 'Current'),
						newTotal: o.new_project_total, revLbl: coRevisionLabel(o), cur: o.currency_id }"></ng-container>

					<portal-button block class="mt-3" (click)="detailsModal.open(o)">View Change Order {{ coRevisionLabel(o) }} Details</portal-button>
					<div class="portal-btn-group co-mobile-actions mt-2" *ngIf="isPending(o)">
						<portal-button icon="wq wq-notes" [title]="preview.ready ? preview.blockedReason : (o.can_comment ? 'Add a comment' : 'No proposal published for this change order')" aria-label="Add a comment" (click)="commentOrder(o)" [disabled]="preview.ready || !o.can_comment"></portal-button>
						<portal-button *ngIf="canConfirm(o)" (click)="openToBeConfirmed(o)" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">To be confirmed</portal-button>
						<portal-button (click)="openReject(o)" [disabled]="preview.ready" [title]="preview.ready ? preview.blockedReason : null">Decline</portal-button>
						<portal-button (click)="acceptOrder(o)" [disabled]="preview.ready || !o.can_accept" [title]="preview.ready ? preview.blockedReason : (o.can_accept ? null : 'No proposal published for this change order')">Accept</portal-button>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<portal-project-footer [projectId]="projectId"></portal-project-footer>

	</ng-container>

	<!-- View Change Order Details modal (own component, shared portal-modal shell) -->
	<portal-change-order-details-modal #detailsModal
		(comment)="commentOrder($event)"
		(confirm)="openToBeConfirmed($event)"
		(reject)="openReject($event)"
		(accept)="acceptOrder($event)"
		(viewProposal)="openProposal($event)"
		(viewChangeOrder)="openChangeOrderPage($event)"></portal-change-order-details-modal>

	<portal-to-be-confirmed-modal #tbcModal (close)="onToBeConfirmed($event)"></portal-to-be-confirmed-modal>
	<portal-reject-modal #rejectModal (close)="onRejected($event)"></portal-reject-modal>
</div>
